/**
 * youtube-resource-stats.js
 *
 * Scans the page for elements carrying a [data-video-id] attribute (rendered
 * by sections/resource-cards.liquid for each "video_resource" metaobject
 * entry), batches those IDs into YouTube Data API v3 `videos.list` calls
 * (up to 50 IDs per request, part=snippet,contentDetails,statistics), and
 * injects the live view count, duration, thumbnail, and description into
 * the matching card markup:
 *
 *   .js-yt-duration       — overlay badge on the thumbnail
 *   .js-yt-duration-meta  — duration shown in the card meta row
 *   .js-yt-views          — view count shown in the card meta row
 *   .js-yt-thumbnail      — <img> to upgrade from the guessed hqdefault.jpg
 *                           to YouTube's actual chosen thumbnail (only
 *                           present on cards with no manually-set thumbnail)
 *   .js-yt-description    — short description pulled from the video's
 *                           YouTube snippet (no manual field for this exists)
 *
 * Results are cached in localStorage for 1 hour per video ID to avoid
 * re-hitting the API (and burning quota) on repeat visits.
 *
 * Requires window.cashmansYouTubeConfig.apiKey to be set (see the inline
 * <script> emitted by resource-cards.liquid, sourced from the section's
 * "YouTube Data API Key" setting).
 */
(function () {
  'use strict';

  // Bump the trailing version number whenever the cached entry shape changes
  // (fields added/removed/renamed) so visitors with an old cached entry from
  // a previous version of this script get a fresh API fetch instead of a
  // stale, incompletely-shaped cache hit for up to an hour.
  var CACHE_PREFIX = 'cashmans_yt_stats_v2_';
  var CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour
  var BATCH_SIZE = 50;

  function getApiKey() {
    return (window.cashmansYouTubeConfig && window.cashmansYouTubeConfig.apiKey) || '';
  }

  function readCache(id) {
    try {
      var raw = window.localStorage.getItem(CACHE_PREFIX + id);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed.t !== 'number') return null;
      if (Date.now() - parsed.t > CACHE_TTL_MS) return null;
      return parsed;
    } catch (e) {
      return null;
    }
  }

  function writeCache(id, entry) {
    try {
      window.localStorage.setItem(
        CACHE_PREFIX + id,
        JSON.stringify({
          d: entry.d,
          v: entry.v,
          thumb: entry.thumb,
          desc: entry.desc,
          t: Date.now()
        })
      );
    } catch (e) {
      // localStorage unavailable/full — fail silently, live data will still render.
    }
  }

  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  // Converts an ISO 8601 duration (e.g. "PT8M42S", "PT1H2M3S") to "M:SS" or "H:MM:SS".
  //
  // YouTube's contentDetails.duration is measured off the encoded frame count
  // and commonly comes back ~1 second longer than the length actually shown
  // in the YouTube player/UI (the player truncates the trailing fractional
  // second; the API rounds it up). We subtract 1 second to match what
  // visitors see on youtube.com — clamped so we never go negative/blank.
  function formatDuration(iso8601) {
    if (!iso8601) return '';
    var match = /^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?$/.exec(iso8601);
    if (!match) return '';
    var hours = parseInt(match[1] || '0', 10);
    var minutes = parseInt(match[2] || '0', 10);
    var seconds = parseInt(match[3] || '0', 10);

    var totalSeconds = hours * 3600 + minutes * 60 + seconds;
    totalSeconds = Math.max(totalSeconds - 1, 0);

    var h = Math.floor(totalSeconds / 3600);
    var m = Math.floor((totalSeconds % 3600) / 60);
    var s = totalSeconds % 60;

    if (h > 0) {
      return h + ':' + pad(m) + ':' + pad(s);
    }
    return m + ':' + pad(s);
  }

  // Picks the highest-resolution thumbnail YouTube has available for a video.
  function pickThumbnailUrl(thumbnails) {
    if (!thumbnails) return '';
    var order = ['maxres', 'standard', 'high', 'medium', 'default'];
    for (var i = 0; i < order.length; i++) {
      if (thumbnails[order[i]] && thumbnails[order[i]].url) {
        return thumbnails[order[i]].url;
      }
    }
    return '';
  }

  // Trims a raw YouTube description down to a short card-friendly excerpt.
  function formatDescription(rawDescription) {
    if (!rawDescription) return '';
    var collapsed = rawDescription.replace(/\s+/g, ' ').trim();
    if (collapsed.length <= 160) return collapsed;
    return collapsed.slice(0, 160).replace(/\s+\S*$/, '') + '…';
  }

  // Converts a raw view count (string or number) to a short form, e.g. "14K views".
  function formatViewCount(rawCount) {
    var n = parseInt(rawCount, 10);
    if (isNaN(n)) return '';

    var value;
    var suffix;
    if (n >= 1000000) {
      value = n / 1000000;
      suffix = 'M';
    } else if (n >= 1000) {
      value = n / 1000;
      suffix = 'K';
    } else {
      return n + ' view' + (n === 1 ? '' : 's');
    }

    var rounded = Math.round(value * 10) / 10;
    var display = rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1);
    return display + suffix + ' views';
  }

  function chunk(array, size) {
    var chunks = [];
    for (var i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  }

  function applyStatsToCards(cards, statsById) {
    cards.forEach(function (card) {
      var id = (card.getAttribute('data-video-id') || '').trim();
      var stats = statsById[id];
      if (!id || !stats) return;

      var durationEls = card.querySelectorAll('.js-yt-duration, .js-yt-duration-meta');
      for (var i = 0; i < durationEls.length; i++) {
        if (stats.d) durationEls[i].textContent = stats.d;
      }

      var viewEls = card.querySelectorAll('.js-yt-views');
      for (var j = 0; j < viewEls.length; j++) {
        if (stats.v) viewEls[j].textContent = stats.v;
      }

      if (stats.thumb) {
        var thumbEls = card.querySelectorAll('.js-yt-thumbnail');
        for (var k = 0; k < thumbEls.length; k++) {
          thumbEls[k].src = stats.thumb;
        }
      }

      if (stats.desc) {
        var descEls = card.querySelectorAll('.js-yt-description');
        for (var m = 0; m < descEls.length; m++) {
          descEls[m].textContent = stats.desc;
        }
      }
    });
  }

  function fetchStatsForIds(ids, apiKey) {
    if (!ids.length) return Promise.resolve({});

    var url =
      'https://www.googleapis.com/youtube/v3/videos' +
      '?part=snippet,contentDetails,statistics' +
      '&id=' + encodeURIComponent(ids.join(',')) +
      '&key=' + encodeURIComponent(apiKey);

    return fetch(url)
      .then(function (response) {
        if (!response.ok) {
          throw new Error('YouTube Data API request failed with status ' + response.status);
        }
        return response.json();
      })
      .then(function (data) {
        var statsById = {};
        (data.items || []).forEach(function (item) {
          var entry = {
            d: formatDuration(item.contentDetails && item.contentDetails.duration),
            v: formatViewCount(item.statistics && item.statistics.viewCount),
            thumb: pickThumbnailUrl(item.snippet && item.snippet.thumbnails),
            desc: formatDescription(item.snippet && item.snippet.description)
          };
          statsById[item.id] = entry;
          writeCache(item.id, entry);
        });
        return statsById;
      })
      .catch(function (error) {
        console.warn('[youtube-resource-stats] Failed to fetch stats for a batch:', error);
        return {};
      });
  }

  function init() {
    var cards = Array.prototype.slice.call(document.querySelectorAll('[data-video-id]'));
    if (!cards.length) return;

    var apiKey = getApiKey();

    var idSet = {};
    cards.forEach(function (card) {
      var id = (card.getAttribute('data-video-id') || '').trim();
      if (id) idSet[id] = true;
    });
    var allIds = Object.keys(idSet);
    if (!allIds.length) return;

    var statsById = {};
    var idsToFetch = [];

    allIds.forEach(function (id) {
      var cached = readCache(id);
      if (cached) {
        statsById[id] = cached;
      } else {
        idsToFetch.push(id);
      }
    });

    // Render whatever we already have cached right away.
    applyStatsToCards(cards, statsById);

    if (!idsToFetch.length) return;

    if (!apiKey) {
      console.warn(
        '[youtube-resource-stats] No YouTube Data API key configured — set one in the ' +
        '"Resources Filter + Cards" section settings to show live view counts & durations.'
      );
      return;
    }

    var batches = chunk(idsToFetch, BATCH_SIZE);
    batches
      .reduce(function (promiseChain, batch) {
        return promiseChain.then(function () {
          return fetchStatsForIds(batch, apiKey).then(function (batchStats) {
            Object.keys(batchStats).forEach(function (id) {
              statsById[id] = batchStats[id];
            });
            applyStatsToCards(cards, statsById);
          });
        });
      }, Promise.resolve())
      .catch(function (error) {
        console.warn('[youtube-resource-stats] Unexpected error fetching video stats:', error);
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
